import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  GraduationCap,
  BookOpen,
  TrendingUp,
  Wallet,
  KeyRound,
  LogOut,
  Menu,
  X,
  ShieldCheck,
  Youtube,
  Users,
  Activity,
  Image as ImageIcon,
  Link2,
  Bell,
  Megaphone,
  MessageSquareQuote,
  Trophy,
  Users2,
  GalleryHorizontalEnd,
  Inbox,
  Briefcase,
  ScrollText,
  Info,
  Building2,
  Award,
  Landmark,
  Package,
  FileBadge,
  UserCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const ROLE_LABEL = {
  institute_admin: "Institute Admin",
  institute_staff: "Institute Staff",
};

const navSections = [
  {
    title: null, // top section: no heading
    items: [
      { to: "/institute", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "i-nav-dashboard" },
      { to: "/institute/courses", label: "Courses", icon: BookOpen, testid: "i-nav-courses" },
      { to: "/institute/departments", label: "Departments", icon: GraduationCap, testid: "i-nav-departments" },
      { to: "/institute/intake", label: "Intake (FY-wise)", icon: TrendingUp, testid: "i-nav-intake" },
      { to: "/institute/fees", label: "Fees (FY-wise)", icon: Wallet, testid: "i-nav-fees" },
    ],
  },
  {
    title: "Website Updates",
    testid: "i-nav-section-website",
    items: [
      { to: "/institute/header-slider", label: "Header Slider", icon: GalleryHorizontalEnd, testid: "i-nav-header-slider" },
      { to: "/institute/info", label: "Institute Information", icon: Info, testid: "i-nav-info" },
      { to: "/institute/mandatory-disclosure", label: "Mandatory Disclosure", icon: ShieldCheck, testid: "i-nav-mandatory-disclosure" },
      { to: "/institute/social-media", label: "Social Media & Home Image", icon: Youtube, testid: "i-nav-social-media" },
      { to: "/institute/home-features", label: "Home Feature Sections", icon: LayoutDashboard, testid: "i-nav-home-features", onlySlugs: ["bulandshahr"] },
      { to: "/institute/scroller", label: "Announcement Scroller", icon: ScrollText, testid: "i-nav-scroller" },
      { to: "/institute/leadership", label: "Leadership Message", icon: MessageSquareQuote, testid: "i-nav-leadership" },
      { to: "/institute/governing-body", label: "Governing Body", icon: Users2, testid: "i-nav-governing-body" },
      { to: "/institute/academics", label: "Academics", icon: BookOpen, testid: "i-nav-academics" },
      { to: "/institute/facilities", label: "Facilities", icon: Building2, testid: "i-nav-facilities" },
      { to: "/institute/alumni", label: "Alumni", icon: Award, testid: "i-nav-alumni" },
      { to: "/institute/alumni-registrations", label: "Alumni Registrations", icon: UserCheck, testid: "i-nav-alumni-reg", adminOnly: true },
      { to: "/institute/events", label: "Campus Engagements", icon: Trophy, testid: "i-nav-events" },
      { to: "/institute/committees", label: "Committees & Cells", icon: Users2, testid: "i-nav-committees" },
      { to: "/institute/gallery", label: "Gallery", icon: ImageIcon, testid: "i-nav-gallery" },
      { to: "/institute/links", label: "Important Links", icon: Link2, testid: "i-nav-links" },
      { to: "/institute/notices", label: "Notices / Circulars", icon: Bell, testid: "i-nav-notices" },
      { to: "/institute/admission-notices", label: "Admission Notices", icon: Megaphone, testid: "i-nav-admission-notices" },
      { to: "/institute/admission-inquiries", label: "Admission Inquiries", icon: Inbox, testid: "i-nav-admission-inquiries" },
      { to: "/institute/jobs", label: "Careers / Jobs", icon: Briefcase, testid: "i-nav-jobs", hideOnSlugs: ["mitschool", "jirt"] },
    ],
  },
  {
    title: "Treasury",
    testid: "i-nav-section-treasury",
    items: [
      { to: "/institute/banks", label: "Bank Accounts", icon: Landmark, testid: "i-nav-banks", adminOnly: true },
      { to: "/institute/movable-items", label: "Movable Items", icon: Package, testid: "i-nav-movables", adminOnly: true },
    ],
  },
  {
    title: "Compliance",
    testid: "i-nav-section-compliance",
    items: [
      { to: "/institute/fra-ffc", label: "FRA / FFC", icon: FileBadge, testid: "i-nav-fra-ffc", adminOnly: true },
      { to: "/institute/naac", label: "NAAC Documents", icon: Award, testid: "i-nav-naac", adminOnly: true, hideForSchool: true },
    ],
  },
  {
    title: null,
    items: [
      { to: "/institute/staff", label: "Staff & Activity", icon: Users, testid: "i-nav-staff", adminOnly: true },
      { to: "/institute/change-password", label: "Change Password", icon: KeyRound, testid: "i-nav-change-password" },
    ],
  },
];

export default function InstituteLayout() {
  const { user, logout, role } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [instituteSlug, setInstituteSlug] = useState("");
  const [instituteType, setInstituteType] = useState("");

  useEffect(() => {
    let cancel = false;
    api.get("/institute/info").then((r) => {
      if (!cancel) {
        setInstituteSlug(r.data?.institute?.website_slug || "");
        setInstituteType(r.data?.institute?.type || "");
      }
    }).catch(() => {});
    return () => { cancel = true; };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/institute/login", { replace: true });
  };

  const isAdmin = role === "institute_admin";
  const isSchool = /school/i.test(instituteType || "");
  const visibleSections = navSections
    .map((s) => ({
      ...s,
      items: s.items.filter((n) =>
        (!n.adminOnly || isAdmin) &&
        !(n.hideForSchool && isSchool) &&
        !(Array.isArray(n.hideOnSlugs) && n.hideOnSlugs.includes(instituteSlug)) &&
        (!Array.isArray(n.onlySlugs) || n.onlySlugs.includes(instituteSlug))
      ),
    }))
    .filter((s) => s.items.length > 0);

  const Sidebar = (
    <aside
      data-testid="institute-sidebar"
      className="w-72 shrink-0 border-r border-[#e5e0d8] bg-[#f9f8f6] flex flex-col"
    >
      <div className="px-6 py-7 border-b border-[#e5e0d8]">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-sm bg-white border border-[#e5e0d8] flex items-center justify-center p-1">
            <img
              src="https://customer-assets.emergentagent.com/job_trust-admin-hub-1/artifacts/i2nc9e38_logo.jpeg"
              alt="GS Mandal crest"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="min-w-0">
            <div className="font-serif text-lg leading-tight text-[#1c1a17] truncate">
              {user?.institute_name || "Institute Portal"}
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] mt-0.5">
              GS Mandal · Institute Portal
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-4 py-6 overflow-y-auto">
        {visibleSections.map((section, sIdx) => (
          <div
            key={section.title || `section-${sIdx}`}
            data-testid={section.testid}
            className={sIdx > 0 ? "mt-5 pt-5 border-t border-[#e5e0d8]" : ""}
          >
            {section.title ? (
              <div className="px-4 mb-2 text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
                {section.title}
              </div>
            ) : null}
            <div className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    data-testid={item.testid}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm transition-colors ${
                        isActive
                          ? "bg-[#234333] text-white"
                          : "text-[#1c1a17] hover:bg-[#efece6]"
                      }`
                    }
                  >
                    <Icon size={16} strokeWidth={1.5} />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="px-4 py-5 border-t border-[#e5e0d8]">
        <div className="px-2 pb-3">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b]">Signed in as</div>
          <div
            data-testid="institute-sidebar-username"
            className="font-sans text-sm text-[#1c1a17] mt-0.5"
          >
            {user?.name || user?.username || "—"}
          </div>
          {role ? (
            <div
              data-testid="institute-sidebar-role"
              className="mt-1 inline-block text-[10px] tracking-wide uppercase border border-[#234333]/30 text-[#234333] px-2 py-0.5 rounded-sm bg-white"
            >
              {ROLE_LABEL[role] || role}
            </div>
          ) : null}
        </div>
        <button
          data-testid="institute-logout-button"
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-4 py-2 border border-[#e5e0d8] rounded-sm text-sm text-[#1c1a17] hover:bg-[#efece6] transition-colors"
        >
          <LogOut size={16} strokeWidth={1.5} />
          Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-paper" data-testid="institute-layout">
      <div className="hidden md:flex">{Sidebar}</div>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 bg-[#f9f8f6] shadow-xl">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-[#e5e0d8] bg-[#f9f8f6]">
          <div className="font-serif text-lg text-[#1c1a17] truncate">
            {user?.institute_name || "Institute"}
          </div>
          <button
            data-testid="institute-mobile-menu"
            onClick={() => setMobileOpen((v) => !v)}
            className="p-2 border border-[#e5e0d8] rounded-sm"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        <main className="flex-1 overflow-y-auto">
          <div className="animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
